import { ClaudeCodeSettingsInstall } from "@/components/claude-code-settings-install";
import { Button } from "@/components/ui/Button";
import { CopyButton } from "@/components/ui/CopyButton";
import { Dialog } from "@/components/ui/Dialog";
import { useRoutes } from "@/routes";
import { useMarketplaceSettings } from "@gram/client/react-query/marketplaceSettings";
import { usePublishStatus } from "@gram/client/react-query/publishStatus";
import { ExternalLink, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AgentProviderPicker } from "@/components/agent-providers/AgentProviderPicker";
import {
  ACTIVE_AGENT_PROVIDER_IDS,
  agentProvidersForSurface,
} from "@/components/agent-providers/agent-providers";
import { CopilotInstallContent } from "../plugins/InstallInstructionsDialog";

function ClaudeInstallContent({
  marketplaceUrl,
  pluginName,
}: {
  marketplaceUrl?: string;
  pluginName?: string;
}) {
  if (!marketplaceUrl) {
    return (
      <p className="text-muted-foreground text-sm italic">
        Publish your plugins to GitHub first to get a marketplace install URL.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* With observability off the marketplace ships no hooks plugin; the
          snippet still registers the marketplace. */}
      {!pluginName && (
        <p className="text-muted-foreground text-sm">
          Your marketplace has no observability plugin yet, so this only
          registers the marketplace.
        </p>
      )}
      <ClaudeCodeSettingsInstall
        marketplaceUrl={marketplaceUrl}
        plugins={pluginName ? [pluginName] : []}
        secretUrl
      />
    </div>
  );
}

function CursorInstallContent() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-semibold">1. Publish the Plugin</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Add the hooks plugin to your Cursor team marketplace and mark it as
          required so it auto-installs for all team members:
        </p>
        <div className="bg-muted/50 p-4 font-mono text-sm">
          <a
            href="https://cursor.com/dashboard/team-content"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 underline underline-offset-4"
          >
            cursor.com/dashboard/team-content
          </a>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">2. Configure Credentials</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          In the Cursor team dashboard, add a{" "}
          <code className="bg-muted px-1 py-0.5 text-xs">Session Start</code>{" "}
          hook that injects your platform credentials. These are automatically
          passed to all subsequent hooks in the session.
        </p>
        <p className="text-muted-foreground mb-4 text-sm">
          Go to{" "}
          <a
            href="https://cursor.com/dashboard/team-content?section=hooks"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 underline underline-offset-4"
          >
            cursor.com/dashboard/team-content
          </a>{" "}
          and create a new hook with:
        </p>
        <div className="bg-muted/50 space-y-3 p-4 text-sm">
          <div className="flex items-baseline gap-2">
            <span className="text-muted-foreground shrink-0 font-medium">
              Hook Name:
            </span>
            <code>Platform Hooks</code>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-muted-foreground shrink-0 font-medium">
              Hook Type:
            </span>
            <code>Command</code>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-muted-foreground shrink-0 font-medium">
              Hook Step:
            </span>
            <code>Session Start</code>
          </div>
          <div>
            <span className="text-muted-foreground font-medium">
              Script Content:
            </span>
            <div className="bg-background/50 mt-1 overflow-x-auto p-3 font-mono text-xs break-all whitespace-pre-wrap">
              {`#!/bin/bash\necho '{"env":{"SPEAKEASY_AI_HOOKS_API_KEY":"`}
              <span className="text-primary font-semibold">{`<YOUR_API_KEY>`}</span>
              {`","SPEAKEASY_AI_HOOKS_PROJECT_SLUG":"`}
              <span className="text-primary font-semibold">{`<YOUR_PROJECT_SLUG>`}</span>
              {`"}}'`}
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-muted-foreground shrink-0 font-medium">
              Platforms:
            </span>
            <code>Mac, Linux</code>
          </div>
        </div>
        <p className="text-muted-foreground mt-2 text-xs">
          Replace{" "}
          <code className="text-primary text-xs">{`<YOUR_API_KEY>`}</code> and{" "}
          <code className="text-primary text-xs">{`<YOUR_PROJECT_SLUG>`}</code>{" "}
          with your platform credentials. Find your API key in your project's
          API Keys settings. This config syncs to all team members
          automatically.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" asChild>
          <a
            href="https://cursor.com/docs/plugins"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="size-4" />
            Plugin Docs
          </a>
        </Button>
        <Button variant="secondary" size="sm" asChild>
          <a
            href="https://cursor.com/docs/hooks"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="size-4" />
            Hooks Docs
          </a>
        </Button>
      </div>
    </div>
  );
}

function CodexInstallContent({
  marketplaceUrl,
  pluginName,
}: {
  marketplaceUrl?: string;
  pluginName?: string;
}) {
  const { data: marketplaceSettings } = useMarketplaceSettings();
  const addCommand = marketplaceUrl
    ? `codex plugin marketplace add ${marketplaceUrl}`
    : null;

  // Codex registers the marketplace under the published marketplace.json
  // `name` (effectiveName) and references plugins as `<plugin>@<name>`.
  const marketplaceKey = marketplaceSettings?.effectiveName ?? null;
  const pluginEntry =
    pluginName && marketplaceKey
      ? `[plugins."${pluginName}@${marketplaceKey}"]\nenabled = true`
      : null;

  const featureFlags = `features.hooks = true\nfeatures.plugin_hooks = true`;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-semibold">
          1. Register the marketplace
        </h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Register your org's published marketplace with Codex:
        </p>
        {addCommand ? (
          <div className="bg-muted/50 p-4 font-mono text-sm">
            <div className="flex items-center justify-between gap-2">
              <code className="break-all">{addCommand}</code>
              <CopyButton size="xs" text={addCommand} tooltip="Copy command" />
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm italic">
            Publish your plugins to GitHub first to get a marketplace URL.
          </p>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">
          2. Enable hooks and the plugin in{" "}
          <code className="text-sm">~/.codex/config.toml</code>
        </h3>
        <p className="text-muted-foreground mb-3 text-sm">
          Hooks are behind a feature flag and the plugin must be explicitly
          enabled. Add all of the following to{" "}
          <code className="bg-muted px-1 py-0.5 text-xs">
            ~/.codex/config.toml
          </code>
          :
        </p>
        <div className="bg-muted/50 p-4 font-mono text-sm">
          <div className="flex items-start justify-between gap-2">
            <pre className="whitespace-pre-wrap">
              {[
                featureFlags,
                pluginEntry ??
                  `[plugins."${pluginName ?? "<plugin-name>"}@${marketplaceKey ?? "<marketplace>"}"]` +
                    `\nenabled = true`,
              ].join("\n\n")}
            </pre>
            <CopyButton
              size="xs"
              text={[featureFlags, pluginEntry ?? ""].join("\n\n").trim()}
              tooltip="Copy config entries"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">
          3. Approve hooks in Codex
        </h3>
        <p className="text-muted-foreground text-sm">
          After restarting Codex, open{" "}
          <code className="bg-muted px-1 py-0.5 text-xs">Settings → Hooks</code>{" "}
          and enable each hook listed under the{" "}
          <code className="bg-muted px-1 py-0.5 text-xs">
            {pluginName ?? "observability"}
          </code>{" "}
          plugin. Codex requires manual approval for each hook event before it
          will fire.
        </p>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">Or install from a ZIP</h3>
        <p className="text-muted-foreground mb-4 text-sm">
          Download a self-contained Codex plugin ZIP from the{" "}
          <strong>Plugins</strong> page (
          <code className="bg-muted px-1 py-0.5 text-xs">
            Download Observability Plugin → Codex
          </code>
          ). The ZIP includes an{" "}
          <code className="bg-muted px-1 py-0.5 text-xs">install.sh</code> that
          handles all three steps automatically:
        </p>
        <div className="bg-muted/50 space-y-1 p-4 font-mono text-sm">
          <code>unzip observability-codex.zip -d ~/gram-observability</code>
          <div className="mt-1">
            <code>bash ~/gram-observability/install.sh</code>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" asChild>
          <a
            href="https://learn.chatgpt.com/docs/hooks"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="size-4" />
            Hooks Docs
          </a>
        </Button>
        <Button variant="secondary" size="sm" asChild>
          <a
            href="https://developers.openai.com/plugins/build/plugins"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="size-4" />
            Plugin Docs
          </a>
        </Button>
      </div>
    </div>
  );
}

type Provider = (typeof providers)[number]["id"];

const providers = agentProvidersForSurface("hooks");

// PublishedRepoPanel surfaces the simpler install path for orgs that have
// already connected a published GitHub repo: a one-click "install the base
// plugin" flow that bakes credentials in, so they can skip the manual
// SessionStart hook below.
function PublishedRepoPanel() {
  const routes = useRoutes();
  return (
    <div className="border-primary/30 bg-primary/5 mb-6 border p-4">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles className="text-primary size-4" />
        <h3 className="text-sm font-semibold">
          Recommended: install via your org's published marketplace
        </h3>
      </div>
      <p className="text-muted-foreground mb-3 text-sm">
        Your org publishes plugins to a private GitHub repo with credentials
        already embedded. Installing the <code>base</code> plugin gives your
        team observability automatically — no manual SessionStart hook, no
        credential paste.
      </p>
      <Button variant="secondary" size="sm" asChild>
        <Link to={routes.plugins.href()}>Go to Plugins</Link>
      </Button>
    </div>
  );
}

export function HooksSetupDialog({
  open,
  onOpenChange,
  defaultProvider = ACTIVE_AGENT_PROVIDER_IDS.hooks[0],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultProvider?: Provider;
}): JSX.Element {
  const [selected, setSelected] = useState<Provider>(defaultProvider);
  const { data: publishStatus } = usePublishStatus();
  const showPublishedPanel =
    publishStatus?.configured &&
    publishStatus?.connected &&
    Boolean(publishStatus?.repoUrl);

  useEffect(() => {
    setSelected(defaultProvider);
  }, [defaultProvider]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="flex max-h-[90vh] max-w-7xl flex-col sm:max-w-7xl">
        <Dialog.Header>
          <Dialog.Title>Setup Hooks</Dialog.Title>
        </Dialog.Header>

        {showPublishedPanel && publishStatus?.repoUrl && <PublishedRepoPanel />}

        <AgentProviderPicker
          aria-label="AI tool"
          providers={providers}
          value={selected}
          onValueChange={setSelected}
        />

        <div className="min-h-0 overflow-y-auto">
          {selected === "claude" && (
            <ClaudeInstallContent
              marketplaceUrl={publishStatus?.marketplaceUrl}
              pluginName={publishStatus?.claudeObservabilityPlugin}
            />
          )}
          {selected === "cursor" && <CursorInstallContent />}
          {selected === "codex" && (
            <CodexInstallContent
              marketplaceUrl={publishStatus?.marketplaceUrl}
              pluginName={publishStatus?.codexObservabilityPlugin}
            />
          )}
          {selected === "copilot" && <CopilotInstallContent />}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
